import { SynChip } from './SynChip';
import { SurfacePagePanel, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { SURFACE_PAGE_SECTION_TITLE } from '@/shared/ui-class';
import { cn } from '@/shared/cn';
import { MeterBar, TrendChart } from '@/versions/v1/modules/_viz';
import {
  displayHeading,
  nodeDisplays,
  trendCaption,
  widgetRows,
  unseededLine,
  type LiveVsDesignView,
  type NodeDisplayKindFilter,
  type NodeDisplayView,
  type RateVsDesignView,
  type TrendVsTargetView,
} from './NodeDisplays.transform';

function RateVsDesignPanel({ display }: { display: RateVsDesignView }) {
  return (
    <SurfacePagePanel title={display.title}>
      <p title={display.provenance} className="text-2xl font-semibold tabular tracking-tight text-ink-primary">
        {display.value}
        <span className="ml-1 text-xs font-semibold tracking-normal text-muted">{display.unit}</span>
      </p>
      <MeterBar
        className="mt-2"
        value={display.value}
        max={display.max}
        marker={{ value: display.design, label: `Design ${display.design} ${display.unit}` }}
        tone={display.tone}
      />
    </SurfacePagePanel>
  );
}

function TrendVsTargetPanel({ display }: { display: TrendVsTargetView }) {
  const caption = trendCaption(display);
  return (
    // The panel's header is drawn here, not by `SurfacePagePanel`: its header
    // action row wraps below a wrapped title, and the comp keeps the [syn]
    // chip at the right of the title's first line (polish.P11 r2). Same
    // card, hairline and title classes as the panel.
    <section className="card-matte-2 flex min-w-0 flex-col">
      <div className="hairline-b flex items-start justify-between gap-2 px-4 pb-2.5 pt-3">
        <h2 className={cn(SURFACE_PAGE_SECTION_TITLE, 'min-w-0')}>{display.title}</h2>
        {display.synthetic ? (
          <span className="mt-1.5 shrink-0">
            <SynChip title={display.provenance} />
          </span>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 px-4 py-3">
        {caption ? <p className="mb-2 text-xs text-muted">{caption}</p> : null}
        <div title={display.provenance} data-display-trend={display.id}>
          <TrendChart
            height={72}
            categories={display.categories}
            series={display.series}
            references={display.references}
            pointMarkers={false}
            tickCount={3}
            referenceLabels={false}
          />
        </div>
      </div>
    </section>
  );
}

function LiveVsDesignPanel({ display }: { display: LiveVsDesignView }) {
  const heading = displayHeading(display.title);
  return (
    <SurfacePagePanel title={heading.title} meta={heading.meta ?? undefined}>
      <Table wrap={false} title={display.provenance}>
        <THead>
          <TR>
            <TH>Parameter</TH>
            <TH className="text-right">Design</TH>
            <TH className="text-right">Live</TH>
          </TR>
        </THead>
        <TBody>
          {display.rows.map((row) => (
            <TR key={row.parameter}>
              <TD className="text-[13px] font-semibold text-ink-primary">{row.parameter}</TD>
              <TD className="whitespace-nowrap text-right text-[13px] tabular text-ink-secondary" title={row.provenance}>
                {row.design}
              </TD>
              <TD
                className={`whitespace-nowrap text-right text-[13px] tabular ${
                  row.live === '—' ? 'text-muted' : 'font-semibold text-ink-primary'
                }`}
                title={row.provenance}
              >
                {row.live}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </SurfacePagePanel>
  );
}

function DisplayPanel({ display }: { display: NodeDisplayView }) {
  if (display.kind === 'rate-vs-design') {
    return <RateVsDesignPanel display={display} />;
  }
  if (display.kind === 'trend-vs-target') {
    return <TrendVsTargetPanel display={display} />;
  }
  return <LiveVsDesignPanel display={display} />;
}

export function NodeDisplays({
  nodeId,
  stack = false,
  tableLast = false,
  only,
  showPending = true,
}: {
  nodeId: string;
  stack?: boolean;
  tableLast?: boolean;
  /** 'tables' renders only the live-vs-design tables (they sit under the operating window); 'widgets' the meters + trends. */
  only?: NodeDisplayKindFilter;
  showPending?: boolean;
}) {
  const displays = nodeDisplays(nodeId, { tableLast, only });
  const pending = showPending ? unseededLine(nodeId) : null;

  if (nodeId === 'sp-crush' && only !== 'tables') {
    return (
      <Commentable name="NodeDisplays">
        <p className="text-sm text-muted">
          No displays for this stage yet.
        </p>
      </Commentable>
    );
  }

  if (displays.length === 0 && !pending) {
    return null;
  }

  return (
    <Commentable name="NodeDisplays">
      <section className="space-y-3">
        {displays.length > 0 ? (
          stack && only === 'widgets' ? (
            <div className="space-y-3">
              {widgetRows(displays).across.map((display) => (
                <DisplayPanel key={display.id} display={display} />
              ))}
              {widgetRows(displays).paired.length ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {widgetRows(displays).paired.map((display) => (
                    <DisplayPanel key={display.id} display={display} />
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className={stack ? 'space-y-3' : 'grid grid-cols-1 gap-3 sm:grid-cols-2'}>
              {displays.map((display) => (
                <DisplayPanel key={display.id} display={display} />
              ))}
            </div>
          )
        ) : null}
        {pending ? <p className="text-xs text-muted">{pending}</p> : null}
      </section>
    </Commentable>
  );
}
